import _ from 'lodash';
import { StateCreator } from 'zustand';
import { createLens } from '@dhmk/zustand-lens';
import { type SchemaVariant } from '@morph-mapper/types';
import { CustomSchemes, PreConfiguration } from '@/types';
import { clearPersistedFile, persistFile } from '../utils/persisted-file';

const USER_ID_STORAGE_KEY = 'mm_user_id';
const JWT_STORAGE_KEY = 'mm_jwt';
const OPERATOR_ID_STORAGE_KEY = 'mm_operator_id';

const getStorage = (kind: 'session' | 'local') =>
  kind === 'local' ? localStorage : sessionStorage;

const loadStored = (
  key: string,
  kind: 'session' | 'local' = 'session',
): string | undefined => {
  try {
    return getStorage(kind).getItem(key) ?? undefined;
  } catch {
    return undefined;
  }
};

const persistStored = (
  key: string,
  value: string,
  kind: 'session' | 'local' = 'session',
) => {
  try {
    getStorage(kind).setItem(key, value);
  } catch (e) { /* storage unavailable */ }
};

const loadStoredOperatorId = (): number | undefined => {
  const operatorId = Number(loadStored(OPERATOR_ID_STORAGE_KEY, 'local'));
  return Number.isInteger(operatorId) && operatorId > 0
    ? operatorId
    : undefined;
};

export type ConfigSlice = {
  id: string | undefined;
  schemaId: string | undefined;
  name: string | undefined;
  file: File | undefined;
  textContent: string | undefined;
  /** Hash of last textContent used for this template (compare across sessions). */
  lastTextContentHash: string | null;
  syncTextContent: string | undefined;
  templateVariant: SchemaVariant | undefined;
  userId: string | undefined;
  JWT: string | undefined;
  operatorId: number | undefined;
  domainAccess: string[];
  domain: string | undefined;
  type: string | undefined;
  preconfiguration: PreConfiguration;
  configSnapshot: Record<string, unknown> | undefined;
  definedCustomSchemes: CustomSchemes | undefined;
  isDeployOnly: boolean;
  isSaveDisabled: boolean;
  isDocModalOpen: boolean;
  isSavingTemplate: boolean;
  isNavTemplateModalOpen: boolean;
  isNavDomainModalOpen: boolean;
  discardFields: string[];
  isUpdateDiff: boolean;

  getId: () => string;
  setId: (id: string) => void;

  getSchemaId: () => string;
  setSchemaId: (schemaId: string) => void;

  getFile: () => File;
  getFileExtension: () => string;
  setFile: (file: File) => void;
  clearFile: () => void;

  files: File[];
  setFiles: (files: File[]) => void;
  getFiles: () => File[];

  setUserId: (userId: string) => void;
  getUserId: () => string;
  setJWT: (JWT: string) => void;
  getJWT: () => string;
  setOperatorId: (operatorId: number) => void;
  getOperatorId: () => number;
  setDomainAccess: (domainAccess: string[]) => void;
  getDomainAccess: () => string[];
  setDomain: (domain: string) => void;
  getDomain: () => string;

  setType: (type: string) => void;
  getType: () => string;

  setVariant: (variant: SchemaVariant) => void;
  getVariant: () => SchemaVariant;

  setName: (name: string) => void;
  getName: () => string;
  setTextContent: (textContent: string) => void;
  getTextContent: () => string;
  setLastTextContentHash: (hash: string | null) => void;
  getLastTextContentHash: () => string | null;
  setSyncTextContent: (syncTextContent: string) => void;
  getSyncTextContent: () => string;
  setIsSavingTemplate: (isSavingTemplate: boolean) => void;
  getIsSavingTemplate: () => boolean;
  setIsDeployOnly: (isDeployOnly: boolean) => void;
  getIsDeployOnly: () => boolean;
  setIsSaveDisabled: (isSaveDisabled: boolean) => void;
  getIsSaveDisabled: () => boolean;
  setIsDocModalOpen: (isDocModalOpen: boolean) => void;
  getIsDocModalOpen: () => boolean;
  setDefinedCustomSchemes: (customSchemes: CustomSchemes) => void;
  getDefinedCustomSchemes: () => CustomSchemes;
  setIsNavTemplateModalOpen: (isNavTemplateModalOpen: boolean) => void;
  getIsNavTemplateModalOpen: () => boolean;
  setisNavDomainModalOpen: (isNavDomainModalOpen: boolean) => void;
  getisNavDomainModalOpen: () => boolean;
  setPreconfiguration: (preconfiguration: PreConfiguration) => void;
  getPreconfiguration: () => PreConfiguration;
  setConfigSnapshot: (
    configSnapshot: Record<string, unknown> | undefined,
  ) => void;
  getConfigSnapshot: () => Record<string, unknown> | undefined;
  updatePreconfiguration: (
    key: string,
    value: { key: string; options: Record<string, any> },
  ) => void;
  setDiscardFields: (discardFields: string[]) => void;
  getDiscardFields: () => string[];
};

export const createConfigSlice: StateCreator<
  { config: ConfigSlice },
  [['zustand/immer', never]],
  [],
  ConfigSlice
> = (_set, _get) => {
  // Slices make use of a namespace, make current slice local namespace.
  const [set, get] = createLens(_set, _get, ['config']);

  return {
    id: undefined,
    schemaId: undefined,
    name: undefined,
    file: undefined,
    files: [],
    textContent: undefined,
    lastTextContentHash: null,
    syncTextContent: undefined,
    templateVariant: undefined,
    userId: loadStored(USER_ID_STORAGE_KEY),
    JWT: loadStored(JWT_STORAGE_KEY),
    operatorId: loadStoredOperatorId(),
    domainAccess: [],
    domain: undefined,
    type: undefined,
    preconfiguration: {},
    configSnapshot: undefined,
    isSavingTemplate: false,
    isDeployOnly: false,
    isSaveDisabled: false,
    isDocModalOpen: false,
    isNavTemplateModalOpen: false,
    isNavDomainModalOpen: false,
    definedCustomSchemes: undefined,
    discardFields: [],
    isUpdateDiff: false,

    getId() {
      const id = get().id;
      if (!id) {
        throw new Error('No id set');
      }
      return id;
    },
    setId(id: string) {
      set((s) => {
        s.id = id;
      });
    },
    getSchemaId() {
      const schemaId = get().schemaId;
      if (!schemaId) {
        throw new Error('No schemaId set');
      }

      return schemaId;
    },
    setSchemaId(schemaId: string) {
      set((s) => {
        s.schemaId = schemaId;
      });
    },
    setUserId(userId) {
      set((s) => {
        s.userId = userId;
      });
      persistStored(USER_ID_STORAGE_KEY, userId);
    },
    getUserId() {
      const userId = get().userId;
      if (!userId) {
        throw new Error('No userId set');
      }
      return userId;
    },
    setJWT(JWT) {
      set((s) => {
        s.JWT = JWT;
      });
      persistStored(JWT_STORAGE_KEY, JWT);
    },
    getJWT() {
      const JWT = get().JWT;
      if (!JWT) {
        throw new Error('No JWT set');
      }
      return JWT;
    },

    setOperatorId(operatorId) {
      set((s) => {
        s.operatorId = operatorId;
      });
      persistStored(OPERATOR_ID_STORAGE_KEY, String(operatorId), 'local');
    },
    getOperatorId() {
      const operatorId = get().operatorId;
      if (!operatorId) {
        throw new Error('No operatorId set');
      }
      return operatorId;
    },

    setDomainAccess(domainAccess) {
      set((s) => {
        s.domainAccess = domainAccess;
      });
    },
    getDomainAccess() {
      const domainAccess = get().domainAccess;
      if (!domainAccess) {
        throw new Error('No domainAccess set');
      }
      return domainAccess;
    },

    setDomain(domain) {
      set((s) => {
        s.domain = domain;
        if (domain === 'update') {
          s.isUpdateDiff = true;
        } else {
          s.isUpdateDiff = false;
        }
      });
    },
    getDomain() {
      const domain = get().domain;
      if (!domain) {
        throw new Error('No domain set');
      }
      return domain;
    },

    setType(type) {
      set((s) => {
        s.type = type;
      });
    },
    getType() {
      const type = get().type;
      if (!type) {
        throw new Error('No type set');
      }
      return type;
    },

    setVariant(variant) {
      set((s) => {
        s.templateVariant = variant;
      });
    },
    getVariant() {
      const variant = get().templateVariant;
      if (!variant) {
        throw new Error('No variant set');
      }
      return variant;
    },

    setName: (name) => {
      set((s) => {
        s.name = name;
      });
    },
    getName: () => {
      const name = get().name;
      if (!name) {
        throw new Error('No name set');
      }
      return name;
    },

    setTextContent: (textContent) =>
      set(() => ({
        textContent,
      })),
    getTextContent: () => {
      const textContent = get().textContent;
      if (!textContent) {
        throw new Error('No text content set');
      }
      return textContent;
    },
    setLastTextContentHash: (hash) =>
      set((s) => {
        s.lastTextContentHash = hash;
      }),
    getLastTextContentHash: () => get().lastTextContentHash,

    setSyncTextContent: (syncTextContent) =>
      set(() => ({
        syncTextContent,
      })),
    getSyncTextContent: () => {
      const syncTextContent = get().syncTextContent;
      if (!syncTextContent) {
        throw new Error('No sync text content set');
      }
      return syncTextContent;
    },

    setDefinedCustomSchemes: (customSchemes) => {
      set((s) => {
        s.definedCustomSchemes = customSchemes;
      });
    },
    getDefinedCustomSchemes: () => {
      const customSchemes = get().definedCustomSchemes;
      if (!customSchemes) {
        throw new Error('No custom schemes set');
      }
      return customSchemes;
    },

    setPreconfiguration: (preconfiguration) => {
      set((s) => {
        s.preconfiguration = preconfiguration;
      });
    },
    getPreconfiguration: () => get().preconfiguration,
    setConfigSnapshot: (configSnapshot) => {
      set((s) => {
        s.configSnapshot = configSnapshot;
      });
    },
    getConfigSnapshot: () => get().configSnapshot,
    updatePreconfiguration: (key, value) => {
      set((s) => {
        const cur = s.preconfiguration[key];
        // only add if new
        if (
          !cur ||
          cur.key !== value.key ||
          !_.isEqual(cur.options, value.options)
        ) {
          s.preconfiguration[key] = value;
        }
      });
    },
    setDiscardFields: (discardFields) => {
      set((s) => {
        s.discardFields = discardFields;
      });
    },
    getDiscardFields: () => get().discardFields,
    getFile: () => {
      const file = get().file;
      if (!file) {
        throw new Error('No file loaded');
      }
      return file;
    },
    getFileExtension: () => {
      const file = get().file;

      if (!file) {
        throw new Error('No file loaded');
      }
      const extenstion = file.name.split('.').pop();
      if (!extenstion) {
        throw new Error('No file extension');
      }

      return extenstion;
    },
    setFile: (file) => {
      set((s) => {
        s.file = file;
      });
      const id = get().id;
      if (id) {
        void persistFile(id, file);
      }
    },

    clearFile: () => {
      set((s) => {
        s.file = undefined;
        s.files = [];
      });
      clearPersistedFile();
    },
    setFiles: (files) => {
      set((s) => {
        s.files = files;
      });
    },
    getFiles: () => get().files,

    setIsSavingTemplate: (isSavingTemplate) => {
      set((s) => {
        s.isSavingTemplate = isSavingTemplate;
      });
    },
    getIsSavingTemplate: () => get().isSavingTemplate,

    setIsDeployOnly: (isDeployOnly) => {
      set((s) => {
        s.isDeployOnly = isDeployOnly;
      });
    },
    getIsDeployOnly: () => get().isDeployOnly,

    setIsSaveDisabled: (isSaveDisabled) => {
      set((s) => {
        s.isSaveDisabled = isSaveDisabled;
      });
    },
    getIsSaveDisabled: () => get().isSaveDisabled,
    setIsDocModalOpen: (isDocModalOpen) => {
      set((s) => {
        s.isDocModalOpen = isDocModalOpen;
      });
    },
    getIsDocModalOpen: () => get().isDocModalOpen,
    setIsNavTemplateModalOpen: (isNavTemplateModalOpen) => {
      set((s) => {
        s.isNavTemplateModalOpen = isNavTemplateModalOpen;
      });
    },
    getIsNavTemplateModalOpen: () => get().isNavTemplateModalOpen,
    setisNavDomainModalOpen: (isNavDomainModalOpen) => {
      set((s) => {
        s.isNavDomainModalOpen = isNavDomainModalOpen;
      });
    },
    getisNavDomainModalOpen: () => get().isNavDomainModalOpen,
  };
};
